import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, normalizeStyle as _normalizeStyle, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "timeline" }
const _hoisted_2 = {
  key: 0,
  class: "timeline-loading"
}
const _hoisted_3 = {
  key: 0,
  class: "weekend-overlay"
}
const _hoisted_4 = ["title"]
const _hoisted_5 = { class: "preview-time-label" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = {
  key: 0,
  class: "timeblock-duration"
}
const _hoisted_9 = {
  key: 0,
  class: "preview-time-label"
}
const _hoisted_10 = {
  key: 1,
  class: "preview-time-label"
}

import { ref, computed, onMounted, onUnmounted } from 'vue';
import moment from 'moment';
import AEmployeeTimeblockTimelineHeader from './a-employee-timeblock-timeline-header.vue';
import type { EmployeeFullData, TimeblockData } from './types';

interface TimelineTask {
  _real_start: Date;
  _real_end: Date;
  subtask?: { title: string };
  blocker?: { type: string };
  type?: string;
  is_draft?: boolean;
}

interface CollisionSegment {
  start: Date;
  end: Date;
}

interface Props {
  isLoading: boolean;
  employee: EmployeeFullData;
  date: Date | string | null;
  scheduledBlocks: TimelineTask[];
  otherTimeblocks?: TimeblockData[];
  currentTimeblockRange: {
    start: Date | null;
    end: Date | null;
  };
  collision: {
    hasCollision: boolean;
    startCollides: boolean;
    endCollides: boolean;
  };
  collisionSegments: CollisionSegment[];
  timeblockDuration: number;
  startHour: number;
  endHour: number;
  hourWidth: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'a-employee-timeblock-timeline',
  props: {
    isLoading: { type: Boolean },
    employee: {},
    date: {},
    scheduledBlocks: {},
    otherTimeblocks: { default: () => [] },
    currentTimeblockRange: {},
    collision: {},
    collisionSegments: {},
    timeblockDuration: {},
    startHour: {},
    endHour: {},
    hourWidth: {}
  },
  emits: ["timeblockUpdated"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const timelineContainer = ref<HTMLElement | null>(null);
const timelineScroll = ref<HTMLElement | null>(null);
const timelineBar = ref<HTMLElement | null>(null);

// Check if mobile device
const isMobile = ref(false);

// Interactive state
const showPreview = ref(false);
const previewBlock = ref<{ start: Date; end: Date } | null>(null);
const dragState = ref({
  isStretching: false,
  isResizingCurrent: false,
  resizeEdge: null as 'left' | 'right' | null,
  startX: 0,
  initialStart: null as Date | null,
  initialEnd: null as Date | null,
});
const resizedCurrentBlock = ref<{ start: Date; end: Date } | null>(null);

// Cursor line state
const showCursor = ref(false);
const cursorPosition = ref<Date | null>(null);

// Check if the current date is a weekend (Saturday or Sunday)
const isWeekend = computed(() => {
  // Use the explicit date prop first, fallback to currentTimeblockRange.start
  const date = props.date || props.currentTimeblockRange.start;
  if (!date) return false;

  const dayOfWeek = moment(date).day(); // 0 = Sunday, 6 = Saturday
  return dayOfWeek === 0 || dayOfWeek === 6;
});

// Check if the current timeblock is outside working hours
const isOutsideWorkingHours = computed(() => {
  if (!props.currentTimeblockRange.start || !props.currentTimeblockRange.end) {
    return false;
  }

  const startHour =
    props.currentTimeblockRange.start.getHours() +
    props.currentTimeblockRange.start.getMinutes() / 60;
  const endHour =
    props.currentTimeblockRange.end.getHours() +
    props.currentTimeblockRange.end.getMinutes() / 60;

  // Check if any part of the timeblock is outside working hours
  const isStartOutside =
    startHour < props.employee.work_start ||
    startHour >= props.employee.work_end;
  const isEndOutside =
    endHour <= props.employee.work_start || endHour > props.employee.work_end;

  return isStartOutside || isEndOutside;
});

// Convert other timeblocks to renderable format
const otherTimeblocksRendered = computed(() => {
  return props.otherTimeblocks
    .map((tb) => {
      if (!tb.date || !tb.starts_at_time || !tb.ends_at_time) return null;

      const [startHour, startMinute] = tb.starts_at_time.split(':').map(Number);
      const [endHour, endMinute] = tb.ends_at_time.split(':').map(Number);

      const start = moment(tb.date)
        .set({ hour: startHour, minute: startMinute, second: 0 })
        .toDate();
      const end = moment(tb.date)
        .set({ hour: endHour, minute: endMinute, second: 0 })
        .toDate();

      return { _start: start, _end: end };
    })
    .filter((tb) => tb !== null);
});

// Position calculations
const getHourPosition = (hour: number): number => {
  return (hour - props.startHour) * props.hourWidth;
};

const getHourWidth = (hours: number): number => {
  return hours * props.hourWidth;
};

const getTimePosition = (date: Date): number => {
  const hour = date.getHours();
  const minute = date.getMinutes();
  const totalHours = hour + minute / 60;
  return (totalHours - props.startHour) * props.hourWidth + 1;
};

const getTimeWidth = (start: Date, end: Date): number => {
  const startHour = start.getHours() + start.getMinutes() / 60;
  const endHour = end.getHours() + end.getMinutes() / 60;
  return (endHour - startHour) * props.hourWidth - 2;
};

// Time snapping utility - snap to 15 minute intervals
const snapToInterval = (date: Date, intervalMinutes = 15): Date => {
  const minutes = date.getMinutes();
  const snappedMinutes =
    Math.round(minutes / intervalMinutes) * intervalMinutes;
  return moment(date)
    .set({ minute: snappedMinutes, second: 0, millisecond: 0 })
    .toDate();
};

// Convert pixel position to time
const positionToTime = (pixelX: number): Date => {
  const hoursFromStart = pixelX / props.hourWidth;
  const totalHours = props.startHour + hoursFromStart;
  const hour = Math.floor(totalHours);
  const minute = (totalHours % 1) * 60;

  // Use current date from currentTimeblockRange or today
  const baseDate = props.currentTimeblockRange.start
    ? moment(props.currentTimeblockRange.start)
    : moment();

  return baseDate.set({ hour, minute, second: 0, millisecond: 0 }).toDate();
};

// Format time as HH:mm
const formatTime = (date: Date): string => {
  return moment(date).format('HH:mm');
};

// Check if mouse is over an existing block (x is already relative position with scroll)
// Note: This does NOT check the current timeblock because we want to allow
// clicking elsewhere to change the current timeblock to a new time
const isOverExistingBlock = (relativeX: number): boolean => {
  // Check if over any scheduled block (saved tasks from database)
  for (const task of props.scheduledBlocks) {
    const blockLeft = getTimePosition(task._real_start);
    const blockWidth = getTimeWidth(task._real_start, task._real_end);
    if (relativeX >= blockLeft && relativeX <= blockLeft + blockWidth) {
      return true;
    }
  }

  // Check if over any other timeblock (from other rows in the modal)
  for (const tb of otherTimeblocksRendered.value) {
    const blockLeft = getTimePosition(tb._start);
    const blockWidth = getTimeWidth(tb._start, tb._end);
    if (relativeX >= blockLeft && relativeX <= blockLeft + blockWidth) {
      return true;
    }
  }

  // Don't check current timeblock - allow changing it by clicking elsewhere
  return false;
};

// Mouse move handler - show preview
const handleMouseMove = (event: MouseEvent) => {
  // Disable hover preview on mobile
  if (isMobile.value) {
    return;
  }

  if (dragState.value.isStretching) {
    handleStretchMove(event);
    return;
  }

  if (dragState.value.isResizingCurrent) {
    handleResizeCurrentMove(event);
    return;
  }

  if (!timelineScroll.value || !timelineBar.value) return;

  // Get position relative to scroll container
  const rect = timelineScroll.value.getBoundingClientRect();
  const scrollLeft = timelineScroll.value.scrollLeft;

  // Calculate X position relative to timeline
  const x = event.clientX - rect.left + scrollLeft;

  // Always calculate cursor position (with 15-min snapping)
  const rawTime = positionToTime(x);
  const snappedTime = snapToInterval(rawTime);
  const cursorHour = snappedTime.getHours() + snappedTime.getMinutes() / 60;

  // Check if cursor is in working hours
  const isInWorkHours =
    cursorHour >= props.employee.work_start &&
    cursorHour < props.employee.work_end;

  // Only show cursor line if in working hours
  if (isInWorkHours) {
    cursorPosition.value = snappedTime;
    showCursor.value = true;
  } else {
    showCursor.value = false;
  }

  // Update cursor style based on work hours
  if (timelineScroll.value) {
    timelineScroll.value.style.cursor = isInWorkHours
      ? 'crosshair'
      : 'not-allowed';
  }

  // Allow preview to show even over existing blocks
  // This allows creating conflicting timeblocks

  // Convert to time and snap
  const snappedStart = snappedTime;

  // Calculate default end time (1 hour later)
  let snappedEnd = moment(snappedStart).add(1, 'hour').toDate();

  // Only show preview if start is within work hours
  if (isInWorkHours) {
    // If the end would exceed work hours, cap it at work_end
    const endHour = snappedEnd.getHours() + snappedEnd.getMinutes() / 60;

    if (endHour > props.employee.work_end) {
      // Create end time at exactly work_end
      snappedEnd = moment(snappedStart)
        .set({
          hour: Math.floor(props.employee.work_end),
          minute: (props.employee.work_end % 1) * 60,
          second: 0,
          millisecond: 0,
        })
        .toDate();
    }

    showPreview.value = true;
    previewBlock.value = {
      start: snappedStart,
      end: snappedEnd,
    };
  } else {
    showPreview.value = false;
  }
};

// Mouse leave handler
const handleMouseLeave = () => {
  if (!dragState.value.isStretching && !dragState.value.isResizingCurrent) {
    showPreview.value = false;
    showCursor.value = false;
    // Reset cursor style to default crosshair
    if (timelineScroll.value) {
      timelineScroll.value.style.cursor = 'crosshair';
    }
  }
};

// Handle mousedown on timeline (including header area)
const handleTimelineMouseDown = (event: MouseEvent) => {
  if (isMobile.value) return;
  if (!timelineScroll.value || !cursorPosition.value) return;

  // Get the target element
  const target = event.target as HTMLElement;

  // Only allow resize handles to work - don't interfere with them
  if (target.classList.contains('resize-handle')) {
    return;
  }

  // Allow clicking anywhere else, including over the current timeblock,
  // to create/redraw the block at the cursor position

  // Allow creating blocks even over existing blocks (scheduled or other timeblocks)
  // This enables creating conflicting timeblocks

  // Check if cursor is in work hours (cursor line must be visible)
  if (!showCursor.value || !showPreview.value || !previewBlock.value) {
    return;
  }

  // Start creating/stretching block at the snapped cursor position
  dragState.value.isStretching = true;
  dragState.value.startX = event.clientX;
  dragState.value.initialStart = cursorPosition.value;

  // Calculate initial end time (1 hour later, capped at work_end)
  let initialEnd = moment(cursorPosition.value).add(1, 'hour').toDate();
  const endHour = initialEnd.getHours() + initialEnd.getMinutes() / 60;
  if (endHour > props.employee.work_end) {
    initialEnd = moment(cursorPosition.value)
      .set({
        hour: Math.floor(props.employee.work_end),
        minute: (props.employee.work_end % 1) * 60,
        second: 0,
        millisecond: 0,
      })
      .toDate();
  }

  dragState.value.initialEnd = initialEnd;

  // Set initial preview block (already has 1 hour duration from preview)
  previewBlock.value = {
    start: cursorPosition.value,
    end: initialEnd,
  };

  event.preventDefault();
};

// Handle stretch move
const handleStretchMove = (event: MouseEvent) => {
  if (!dragState.value.initialStart || !timelineScroll.value) return;

  const rect = timelineScroll.value.getBoundingClientRect();
  const x = event.clientX - rect.left + timelineScroll.value.scrollLeft;

  const currentTime = positionToTime(x);
  let snappedTime = snapToInterval(currentTime);

  const initialStartTime = dragState.value.initialStart.getTime();
  const snappedTimeValue = snappedTime.getTime();

  if (snappedTimeValue >= initialStartTime) {
    // Stretching right (extending end)
    // Ensure minimum 15-minute block
    let endTime = snappedTime;
    if (snappedTimeValue === initialStartTime) {
      endTime = moment(snappedTime).add(15, 'minutes').toDate();
    }

    // Cap at work_end
    const endHour = endTime.getHours() + endTime.getMinutes() / 60;
    if (endHour > props.employee.work_end) {
      endTime = moment(endTime)
        .set({
          hour: Math.floor(props.employee.work_end),
          minute: (props.employee.work_end % 1) * 60,
          second: 0,
          millisecond: 0,
        })
        .toDate();
    }

    previewBlock.value = {
      start: dragState.value.initialStart,
      end: endTime,
    };

    // Update cursor to show at the actual block edge (end)
    cursorPosition.value = endTime;
    showCursor.value = true;
  } else {
    // Stretching left (extending start)
    // Cap at work_start
    const snappedStartHour =
      snappedTime.getHours() + snappedTime.getMinutes() / 60;
    if (snappedStartHour < props.employee.work_start) {
      snappedTime = moment(snappedTime)
        .set({
          hour: Math.floor(props.employee.work_start),
          minute: (props.employee.work_start % 1) * 60,
          second: 0,
          millisecond: 0,
        })
        .toDate();
    }

    previewBlock.value = {
      start: snappedTime,
      end: dragState.value.initialStart,
    };

    // Update cursor to show at the actual block edge (start)
    cursorPosition.value = snappedTime;
    showCursor.value = true;
  }
};

// Handle stretch end
const handleStretchEnd = () => {
  if (dragState.value.isStretching && previewBlock.value) {
    // Only emit if we have a valid block (start != end)
    const startTime = previewBlock.value.start.getTime();
    const endTime = previewBlock.value.end.getTime();

    if (endTime > startTime) {
      // Emit the new timeblock
      emit('timeblockUpdated', {
        starts_at_time: formatTime(previewBlock.value.start),
        ends_at_time: formatTime(previewBlock.value.end),
      });
    }
  }

  dragState.value.isStretching = false;
  dragState.value.initialStart = null;
  dragState.value.initialEnd = null;
  showPreview.value = false;
};

// Resize handle mouse down - start resizing
const handleResizeMouseDown = (event: MouseEvent, edge: 'left' | 'right') => {
  if (!props.currentTimeblockRange.start || !props.currentTimeblockRange.end)
    return;

  dragState.value.isResizingCurrent = true;
  dragState.value.resizeEdge = edge;
  dragState.value.startX = event.clientX;
  dragState.value.initialStart = props.currentTimeblockRange.start;
  dragState.value.initialEnd = props.currentTimeblockRange.end;

  resizedCurrentBlock.value = {
    start: props.currentTimeblockRange.start,
    end: props.currentTimeblockRange.end,
  };

  event.preventDefault();
};

// Handle resize current block move
const handleResizeCurrentMove = (event: MouseEvent) => {
  if (
    !dragState.value.initialStart ||
    !dragState.value.initialEnd ||
    !timelineScroll.value
  )
    return;

  const rect = timelineScroll.value.getBoundingClientRect();
  const x = event.clientX - rect.left + timelineScroll.value.scrollLeft;

  const currentTime = positionToTime(x);
  let snappedTime = snapToInterval(currentTime);

  if (dragState.value.resizeEdge === 'left') {
    // Resizing left edge (changing start time)
    // Cap at work_start
    const snappedStartHour =
      snappedTime.getHours() + snappedTime.getMinutes() / 60;
    if (snappedStartHour < props.employee.work_start) {
      snappedTime = moment(snappedTime)
        .set({
          hour: Math.floor(props.employee.work_start),
          minute: (props.employee.work_start % 1) * 60,
          second: 0,
          millisecond: 0,
        })
        .toDate();
    }

    // Make sure new start is before end
    if (snappedTime < dragState.value.initialEnd) {
      resizedCurrentBlock.value = {
        start: snappedTime,
        end: dragState.value.initialEnd,
      };
      // Update cursor to show at the actual block edge
      cursorPosition.value = snappedTime;
      showCursor.value = true;
    }
  } else if (dragState.value.resizeEdge === 'right') {
    // Resizing right edge (changing end time)
    // Cap at work_end
    const snappedEndHour =
      snappedTime.getHours() + snappedTime.getMinutes() / 60;
    if (snappedEndHour > props.employee.work_end) {
      snappedTime = moment(snappedTime)
        .set({
          hour: Math.floor(props.employee.work_end),
          minute: (props.employee.work_end % 1) * 60,
          second: 0,
          millisecond: 0,
        })
        .toDate();
    }

    // Make sure new end is after start
    if (snappedTime > dragState.value.initialStart) {
      resizedCurrentBlock.value = {
        start: dragState.value.initialStart,
        end: snappedTime,
      };
      // Update cursor to show at the actual block edge
      cursorPosition.value = snappedTime;
      showCursor.value = true;
    }
  }
};

// Handle resize current block end
const handleResizeCurrentEnd = () => {
  if (dragState.value.isResizingCurrent && resizedCurrentBlock.value) {
    // Emit the updated timeblock
    emit('timeblockUpdated', {
      starts_at_time: formatTime(resizedCurrentBlock.value.start),
      ends_at_time: formatTime(resizedCurrentBlock.value.end),
    });
  }

  dragState.value.isResizingCurrent = false;
  dragState.value.resizeEdge = null;
  dragState.value.initialStart = null;
  dragState.value.initialEnd = null;
  resizedCurrentBlock.value = null;
};

// Global mouse up handler
const handleGlobalMouseUp = () => {
  if (dragState.value.isStretching) {
    handleStretchEnd();
  } else if (dragState.value.isResizingCurrent) {
    handleResizeCurrentEnd();
  }
};

// Global mouse move handler
const handleGlobalMouseMove = (event: MouseEvent) => {
  if (dragState.value.isStretching) {
    handleStretchMove(event);
  } else if (dragState.value.isResizingCurrent) {
    handleResizeCurrentMove(event);
  }
};

// Setup global listeners
onMounted(() => {
  // Detect mobile device
  isMobile.value = window.innerWidth <= 768;

  // Listen for resize to update mobile status
  const handleResize = () => {
    isMobile.value = window.innerWidth <= 768;
  };
  window.addEventListener('resize', handleResize);

  document.addEventListener('mouseup', handleGlobalMouseUp);
  document.addEventListener('mousemove', handleGlobalMouseMove);
});

onUnmounted(() => {
  document.removeEventListener('mouseup', handleGlobalMouseUp);
  document.removeEventListener('mousemove', handleGlobalMouseMove);
});

// Expose refs for parent component
__expose({
  timelineScroll,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.isLoading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[2] || (_cache[2] = [
          _createElementVNode("span", null, "Načítavam...", -1)
        ]))]))
      : (_openBlock(), _createElementBlock("div", {
          key: 1,
          ref_key: "timelineContainer",
          ref: timelineContainer,
          class: "timeline__content"
        }, [
          _createElementVNode("div", {
            ref_key: "timelineScroll",
            ref: timelineScroll,
            class: "timeline-scroll",
            onMousemove: handleMouseMove,
            onMouseleave: handleMouseLeave,
            onMousedown: handleTimelineMouseDown
          }, [
            _createVNode(AEmployeeTimeblockTimelineHeader, {
              "start-hour": __props.startHour,
              "end-hour": __props.endHour,
              "hour-width": __props.hourWidth
            }, null, 8, ["start-hour", "end-hour", "hour-width"]),
            _createElementVNode("div", {
              ref_key: "timelineBar",
              ref: timelineBar,
              class: "timeline-bar"
            }, [
              _createElementVNode("div", {
                class: "non-working-hours",
                style: _normalizeStyle({
              left: '0px',
              width: `${getHourPosition(__props.employee.work_start)}px`,
            })
              }, null, 4),
              _createElementVNode("div", {
                class: "non-working-hours",
                style: _normalizeStyle({
              left: `${getHourPosition(__props.employee.work_end)}px`,
              width: `${getHourWidth(24 - __props.employee.work_end)}px`,
            })
              }, null, 4),
              _createElementVNode("div", {
                class: "work-hours-bar",
                style: _normalizeStyle({
              left: `${getHourPosition(__props.employee.work_start)}px`,
              width: `${getHourWidth(
                __props.employee.work_end - __props.employee.work_start,
              )}px`,
            })
              }, null, 4),
              (isWeekend.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.scheduledBlocks, (task, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: `task-${index}`,
                  class: _normalizeClass(["scheduled-block", {
              'scheduled-block--blocker': [
                'blocker',
                'adhoc',
                'home_office',
                'vacation',
                'free_day',
                'office365_blocker',
              ].includes(task.type),
              'scheduled-block--concept': task.is_draft === true,
            }]),
                  style: _normalizeStyle({
              left: `${getTimePosition(task._real_start)}px`,
              width: `${getTimeWidth(task._real_start, task._real_end)}px`,
            }),
                  title: `${task.subtask?.title || task.blocker?.type || 'Task'}`
                }, null, 14, _hoisted_4))
              }), 128)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(otherTimeblocksRendered.value, (timeblock, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: `other-${index}`,
                  class: "other-timeblock",
                  style: _normalizeStyle({
              left: `${getTimePosition(timeblock._start)}px`,
              width: `${getTimeWidth(timeblock._start, timeblock._end)}px`,
            }),
                  title: `Other timeblock`
                }, null, 4))
              }), 128)),
              (showPreview.value && previewBlock.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: _normalizeClass(["preview-block", { 'is-stretching': dragState.value.isStretching }]),
                    style: _normalizeStyle({
              left: `${getTimePosition(previewBlock.value.start)}px`,
              width: `${getTimeWidth(previewBlock.value.start, previewBlock.value.end)}px`,
            })
                  }, [
                    _createElementVNode("div", _hoisted_5, [
                      (getTimeWidth(previewBlock.value.start, previewBlock.value.end) <= 58)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(formatTime(previewBlock.value.start)), 1))
                        : (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(formatTime(previewBlock.value.start)) + " - " + _toDisplayString(formatTime(previewBlock.value.end)), 1))
                    ])
                  ], 6))
                : _createCommentVNode("", true),
              (
              __props.currentTimeblockRange.start &&
              __props.currentTimeblockRange.end &&
              !dragState.value.isResizingCurrent
            )
                ? (_openBlock(), _createElementBlock("div", {
                    key: 2,
                    class: _normalizeClass(["current-timeblock", {
              'has-collision': __props.collision.hasCollision,
              'outside-working-hours': isOutsideWorkingHours.value,
            }]),
                    style: _normalizeStyle({
              left: `${getTimePosition(__props.currentTimeblockRange.start)}px`,
              width: `${getTimeWidth(
                __props.currentTimeblockRange.start,
                __props.currentTimeblockRange.end,
              )}px`,
            })
                  }, [
                    (__props.timeblockDuration > 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(__props.timeblockDuration.toFixed(2)) + "h ", 1))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", {
                      class: "resize-handle resize-handle--left",
                      onMousedown: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (handleResizeMouseDown($event, 'left')), ["stop"]))
                    }, null, 32),
                    _createElementVNode("div", {
                      class: "resize-handle resize-handle--right",
                      onMousedown: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (handleResizeMouseDown($event, 'right')), ["stop"]))
                    }, null, 32)
                  ], 6))
                : _createCommentVNode("", true),
              (dragState.value.isResizingCurrent && resizedCurrentBlock.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 3,
                    class: _normalizeClass(["current-timeblock is-resizing", {
              'has-collision': __props.collision.hasCollision,
              'outside-working-hours': isOutsideWorkingHours.value,
            }]),
                    style: _normalizeStyle({
              left: `${getTimePosition(resizedCurrentBlock.value.start)}px`,
              width: `${getTimeWidth(
                resizedCurrentBlock.value.start,
                resizedCurrentBlock.value.end,
              )}px`,
            })
                  }, [
                    (
                getTimeWidth(
                  resizedCurrentBlock.value.start,
                  resizedCurrentBlock.value.end,
                ) <= 58
              )
                      ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(formatTime(resizedCurrentBlock.value.start)), 1))
                      : (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(formatTime(resizedCurrentBlock.value.start)) + " - " + _toDisplayString(formatTime(resizedCurrentBlock.value.end)), 1))
                  ], 6))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.collisionSegments, (segment, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: `collision-${index}`,
                  class: "collision-segment",
                  style: _normalizeStyle({
              left: `${getTimePosition(segment.start)}px`,
              width: `${getTimeWidth(segment.start, segment.end)}px`,
            })
                }, null, 4))
              }), 128)),
              (showCursor.value && cursorPosition.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 4,
                    class: "timeline-cursor",
                    style: _normalizeStyle({
              left: `${getTimePosition(cursorPosition.value)}px`,
            })
                  }, null, 4))
                : _createCommentVNode("", true)
            ], 512)
          ], 544)
        ], 512))
  ]))
}
}

})